import type { TaskSelfItem } from '@/client/api/tasksSelf';
import { isRecord } from '@/client/utils/typeGuards';
import type {
  StoryboardPreviewSourceMetadata,
  StoryboardSegment,
  StoryboardSegmentPreviewEntry,
} from '../types';

const SORA_MODEL_KEYS = new Set(['sora-2-async', 'sora-2-async-remix']);

const VIDEO_ID_PREFIXES = ['video_', 'vd_', 'soravideo_', 'vid_'];

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const normalizeModelId = (value?: string | null): string => {
  if (!isNonEmptyString(value)) {
    return '';
  }
  return value.trim().toLowerCase();
};

export const isSoraModelId = (modelId?: string | null): boolean => {
  const normalized = normalizeModelId(modelId);
  return normalized ? SORA_MODEL_KEYS.has(normalized) : false;
};

const isLikelyVideoId = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  const lower = trimmed.toLowerCase();
  if (VIDEO_ID_PREFIXES.some((prefix) => lower.startsWith(prefix))) {
    return true;
  }
  return /^[a-z0-9_-]{8,}$/i.test(trimmed);
};

const pickCandidateString = (value: unknown): string | undefined => {
  if (!isNonEmptyString(value)) {
    return undefined;
  }
  let trimmed = value.trim();
  const soraScheme = 'storyboard-sora://';
  if (trimmed.toLowerCase().startsWith(soraScheme)) {
    trimmed = trimmed.slice(soraScheme.length);
  }
  if (!isLikelyVideoId(trimmed)) {
    return undefined;
  }
  return trimmed;
};

const VIDEO_ID_CANDIDATE_KEYS = [
  'id',
  'video_id',
  'videoId',
  'request_id',
  'requestId',
  'detail_id',
  'detailId',
  'video',
  'video_url',
  'videoUrl',
  'remixed_from_video_id',
  'original_video_id',
  'data_id',
  'dataId',
] as const;

const searchVideoId = (value: unknown, depth = 0, seen = new WeakSet<object>()): string | undefined => {
  if (value == null) return undefined;
  if (depth > 5) return undefined;

  if (typeof value === 'string') {
    return pickCandidateString(value);
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const nested = searchVideoId(item, depth + 1, seen);
      if (nested) {
        return nested;
      }
    }
    return undefined;
  }

  if (!isRecord(value)) {
    return undefined;
  }

  if (seen.has(value)) {
    return undefined;
  }
  seen.add(value);

  for (const key of VIDEO_ID_CANDIDATE_KEYS) {
    if (!(key in value)) continue;
    const candidate = pickCandidateString(value[key]);
    if (candidate) {
      return candidate;
    }
  }

  for (const nestedValue of Object.values(value)) {
    const nested = searchVideoId(nestedValue, depth + 1, seen);
    if (nested) {
      return nested;
    }
  }

  return undefined;
};

const collectStringCandidates = (...values: Array<unknown>): string[] => {
  const result: string[] = [];
  for (const value of values) {
    const candidate = pickCandidateString(value);
    if (candidate) {
      result.push(candidate);
    }
  }
  return result;
};

const collectPreviewEntryVideoIdCandidates = (entry: StoryboardSegmentPreviewEntry): string[] => {
  const candidates: string[] = [];
  if (entry.downloadSource?.type === 'sora') {
    candidates.push(entry.downloadSource.videoId);
  }
  if (entry.taskId) {
    candidates.push(entry.taskId);
  }
  if (entry.url) {
    candidates.push(entry.url);
  }
  return candidates;
};

export const guessSoraVideoIdFromPreviewEntry = (
  entry: StoryboardSegmentPreviewEntry | null | undefined
): string | undefined => {
  if (!entry) {
    return undefined;
  }
  const candidates = collectPreviewEntryVideoIdCandidates(entry);
  for (const candidate of candidates) {
    const picked = pickCandidateString(candidate);
    if (picked) {
      return picked;
    }
  }
  return undefined;
};

export const extractSoraVideoIdFromTask = (
  task: TaskSelfItem | null | undefined,
  fallbacks: Array<string | undefined | null> = []
): string | undefined => {
  if (!task) {
    return undefined;
  }

  const direct = searchVideoId(task);
  if (direct) {
    return direct;
  }

  const fromData = searchVideoId(task.data);
  if (fromData) {
    return fromData;
  }

  const fromProperties = searchVideoId(task.properties);
  if (fromProperties) {
    return fromProperties;
  }

  const additional = collectStringCandidates(
    (task as Record<string, unknown>)?.video,
    (task as Record<string, unknown>)?.video_id,
    (task as Record<string, unknown>)?.videoId,
    task.task_id,
    (task as Record<string, unknown>)?.taskId,
    (task as Record<string, unknown>)?.request_id,
    (task as Record<string, unknown>)?.requestId
  );

  if (additional.length) {
    return additional[0];
  }

  for (const fallback of fallbacks) {
    const candidate = pickCandidateString(fallback);
    if (candidate) {
      return candidate;
    }
  }

  return undefined;
};

const collectSegmentVideoIdCandidates = (segment: StoryboardSegment): string[] => {
  const candidates: string[] = [];
  if (segment.taskId) {
    candidates.push(segment.taskId);
  }
  if (segment.previewUrl) {
    candidates.push(segment.previewUrl);
  }
  for (const entry of segment.previewEntries) {
    if (entry.taskId) {
      candidates.push(entry.taskId);
    }
    if (entry.downloadSource?.type === 'sora') {
      candidates.push(entry.downloadSource.videoId);
    }
    if (entry.url) {
      candidates.push(entry.url);
    }
  }
  return candidates;
};

export const guessSoraVideoIdFromSegment = (
  segment: StoryboardSegment | null | undefined
): string | undefined => {
  if (!segment) {
    return undefined;
  }
  if (!isSoraModelId(segment.modelId)) {
    return undefined;
  }
  const candidates = collectSegmentVideoIdCandidates(segment);
  for (const candidate of candidates) {
    const picked = pickCandidateString(candidate);
    if (picked) {
      return picked;
    }
  }
  return undefined;
};

const METADATA_SCAN_MAX_DEPTH = 6;
const METADATA_ENDPOINT_KEYS = ['endpoint', 'api_endpoint', 'apiEndpoint', 'request_endpoint', 'requestEndpoint', 'path'] as const;
const METADATA_MODEL_KEYS = ['model', 'model_id', 'modelId'] as const;
const METADATA_MODEL_KEY_KEYS = ['model_key', 'modelKey'] as const;
const METADATA_PRIVATE_KEYS = ['private', 'isPrivate'] as const;

const parseBooleanLike = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }
    if (['true', '1', 'yes', 'y'].includes(normalized)) {
      return true;
    }
    if (['false', '0', 'no', 'n'].includes(normalized)) {
      return false;
    }
    return undefined;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return undefined;
    }
    if (value === 1) {
      return true;
    }
    if (value === 0) {
      return false;
    }
  }
  return undefined;
};

const findStringByKeys = (
  value: unknown,
  keys: readonly string[],
  depth = 0,
  seen = new WeakSet<object>()
): string | undefined => {
  if (value == null || depth > METADATA_SCAN_MAX_DEPTH) {
    return undefined;
  }
  if (typeof value === 'string') {
    return undefined;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findStringByKeys(item, keys, depth + 1, seen);
      if (found) {
        return found;
      }
    }
    return undefined;
  }
  if (!isRecord(value)) {
    return undefined;
  }
  if (seen.has(value)) {
    return undefined;
  }
  seen.add(value);
  for (const key of keys) {
    if (key in value) {
      const candidate = value[key];
      if (typeof candidate === 'string') {
        const trimmed = candidate.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }
  }
  for (const nested of Object.values(value)) {
    const found = findStringByKeys(nested, keys, depth + 1, seen);
    if (found) {
      return found;
    }
  }
  return undefined;
};

const findBooleanByKeys = (
  value: unknown,
  keys: readonly string[],
  depth = 0,
  seen = new WeakSet<object>()
): boolean | undefined => {
  if (value == null || depth > METADATA_SCAN_MAX_DEPTH) {
    return undefined;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findBooleanByKeys(item, keys, depth + 1, seen);
      if (found !== undefined) {
        return found;
      }
    }
    return undefined;
  }
  if (!isRecord(value)) {
    return undefined;
  }
  if (seen.has(value)) {
    return undefined;
  }
  seen.add(value);
  for (const key of keys) {
    if (key in value) {
      const candidate = parseBooleanLike(value[key]);
      if (candidate !== undefined) {
        return candidate;
      }
    }
  }
  for (const nested of Object.values(value)) {
    const found = findBooleanByKeys(nested, keys, depth + 1, seen);
    if (found !== undefined) {
      return found;
    }
  }
  return undefined;
};

const collectMetadataCandidates = (task: TaskSelfItem): Record<string, unknown>[] => {
  const result: Record<string, unknown>[] = [];
  const pushRecord = (value: unknown) => {
    if (isRecord(value)) {
      result.push(value);
    }
  };
  pushRecord(task as unknown as Record<string, unknown>);
  pushRecord(task.data);
  pushRecord(task.properties);
  return result;
};

export const extractSoraPreviewSourceMetadataFromTask = (
  task: TaskSelfItem | null | undefined
): StoryboardPreviewSourceMetadata | undefined => {
  if (!task) {
    return undefined;
  }
  const candidates = collectMetadataCandidates(task);
  let endpoint: string | undefined;
  let modelId: string | undefined;
  let modelKey: string | undefined;
  let isPrivate: boolean | undefined;

  for (const candidate of candidates) {
    if (!endpoint) {
      endpoint = findStringByKeys(candidate, METADATA_ENDPOINT_KEYS);
    }
    if (!modelId) {
      modelId = findStringByKeys(candidate, METADATA_MODEL_KEYS);
    }
    if (!modelKey) {
      modelKey = findStringByKeys(candidate, METADATA_MODEL_KEY_KEYS);
    }
    if (isPrivate === undefined) {
      isPrivate = findBooleanByKeys(candidate, METADATA_PRIVATE_KEYS);
    }
  }

  const normalizedEndpoint = endpoint?.trim();
  const normalizedModelId = modelId?.trim();
  const normalizedModelKey = modelKey?.trim();

  if (!normalizedEndpoint && !normalizedModelId && !normalizedModelKey && isPrivate === undefined) {
    return undefined;
  }

  return {
    endpoint: normalizedEndpoint,
    modelId: normalizedModelId,
    modelKey: normalizedModelKey,
    ...(isPrivate === undefined ? {} : { isPrivate }),
  };
};
